import React, { useState, useEffect } from 'react';
import { RootState } from '../../../store/store';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import {
  Grid,
  GridColumn as Column,
  GridPageChangeEvent,
  GridSortChangeEvent,
  GridCellProps,
} from '@progress/kendo-react-grid';
import { useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import api from '../../../utils/api';
import { PagerTargetEvent } from '@progress/kendo-react-data-tools';
import { orderBy } from '@progress/kendo-data-query';
import '../PatientPage.css';
import { GenderCode } from '@types';
import { genderMap } from 'store/types/GenderCode';
import { PatientFhirSend } from 'store/types/PatientFhirSend';

interface Patient {
  id: number;
  ptCd: string;
  name: string;
  gender: GenderCode;
  birthDate: string;
  patientWorkoutInfo?: {
    phiCode: string;
    fileName: string;
  }[];
}

const PatientListComponent = () => {
  const [patients, setPatients] = useState<Patient[]>([]);
  const [filteredPatients, setFilteredPatients] = useState<Patient[]>([]); // 필터링된 데이터
  const [searchQuery, setSearchQuery] = useState(""); // 검색어 상태 추가
  const [sort, setSort] = useState<{ field: string, dir: "asc" | "desc" | undefined }[]>([]);
  const [page, setPage] = useState({ skip: 0, take: patients?.length || 20 });
  const user = useSelector((state: RootState) => state.user.user);
  const dispatch = useAppDispatch();
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());


  const [pageSizeValue, setPageSizeValue] = React.useState<
    number | string | undefined
  >();

  useEffect(() => {
    const fetchPatients = async () => {
      if (user?.hospital?.hspCd) {
        try {
          const response = await api.getPatientListWithPdfAsync(user.hospital.hspCd);
          setPatients(
            response.data.map((patient: Patient) => ({
              ...patient,
              gender: genderMap[patient.gender] || patient.gender
            })))
          setFilteredPatients(response.data); // 필터링 데이터 초기화
        } catch (error) {
          console.error('Error fetching patients:', error);
        }
      }
    };

    console.log('patients 데이터:', patients);
    fetchPatients();
  }, []); // 빈 배열로 설정하여 첫 렌더링 시 한 번만 실행

  const onSortChange = (e: GridSortChangeEvent) => {
    setSort(
      e.sort as Array<{ field: string; dir: 'asc' | 'desc' | undefined }>
    );
    setPatients(orderBy(patients, e.sort));
  };

  const onPageChange = (e: GridPageChangeEvent) => {
    const targetEvent = e.targetEvent as PagerTargetEvent;
    const take = targetEvent.value === 'All' ? patients.length : e.page.take;

    if (targetEvent.value) {
      setPageSizeValue(targetEvent.value);
    }
    setPage({
      ...e.page,
      take,
    });
  };

  const onSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value.toLowerCase();
    setSearchQuery(query);

     // 필터링 로직
     const filtered = patients.filter((patient) =>
      patient.name.toLowerCase().includes(query)
    );
    setFilteredPatients(filtered);
    setPage({ skip: 0, take: 20 }); // 검색 시 페이지 초기화
  };
  
  const CheckboxCell = (props: GridCellProps) => {
    const isSelected = selectedIds.has(props.dataItem.id);
    const handleChange = () => {
      const newSelected = new Set(selectedIds);
      if (isSelected) newSelected.delete(props.dataItem.id);
      else newSelected.add(props.dataItem.id);
      setSelectedIds(newSelected);
    };
    return (
      <td>
        <input type="checkbox" checked={isSelected} onChange={handleChange} />
      </td>
    );
  };

  
  const HeaderCheckbox = () => {
    console.log('patients 데이터:', patients);

    const currentPageData = filteredPatients.slice(page.skip, page.skip + page.take);
    const allSelected = currentPageData.every((p) => selectedIds.has(p.id));

    const handleHeaderChange = () => {
      const newSelected = new Set(selectedIds);
      if (allSelected) {
        currentPageData.forEach((p) => newSelected.delete(p.id));
      } else {
        currentPageData.forEach((p) => newSelected.add(p.id));
      }
      setSelectedIds(newSelected);
    };

    return (
      <input type="checkbox" checked={allSelected} onChange={handleHeaderChange} />
    );
  };


  const sendSelectedPatientFiles = async () => {
    const selectedPatients = patients.filter(p => selectedIds.has(p.id));
  
    if (selectedPatients.length === 0) {
      alert('전송할 환자를 선택해주세요.');
      return;
    }
  
    const filesToSend: PatientFhirSend[] = [];
  
    selectedPatients.forEach((patient) => {
      patient.patientWorkoutInfo?.forEach((info) => {
        filesToSend.push({
          phiCode: info.phiCode,
          fileName: info.fileName
        });
      });
    });
    console.log("전송된 파일들:", filesToSend);

    try {
      await api.sendPatientFhirDocumentAsync(filesToSend);
      console.log("전송된 파일들:", filesToSend);
      alert("파일 전송이 완료되었습니다.");
    } catch (error) {
      console.error("전송 중 오류", error);
      alert("파일 전송에 실패했습니다.");
    }
  };
  

  const DetailLinkCell = (props: GridCellProps) => {
    const hspCd = user?.hospital?.hspCd;
    let linkPath: string | null = null;

    switch (hspCd){
      case '01':
        linkPath = `/patientDetail/01/${props.dataItem.id}`;
        break;
      case '02':
        linkPath = `/patientDetail/02/${props.dataItem.id}`;
        break;
      default:
        linkPath = null;
        break;
    }

    return (
      <td>
        {linkPath ? (
          <Link to={linkPath} className="link">
            {props.dataItem.name}
          </Link>
        ) : (
          <span className="no-link">{props.dataItem.name}</span>
        )}
      </td>
    );
  };

  const HeaderCheckboxCell = () => (
    <th>
      <HeaderCheckbox />
    </th>
  );

  return (
    <div className="PatientListContainer layoutContainer">
      <h2>환자 리스트</h2>
       
       {/* 검색 입력 필드 */}
      <input
        type="text"
        placeholder="환자 이름 검색"
        value={searchQuery}
        onChange={onSearchChange}
        className="search-input"
        style={{ marginBottom: "10px", padding: "5px", width: "300px" }}
      />
      <button
        onClick={sendSelectedPatientFiles}
        style={{ marginBottom: '10px', padding: '6px 14px' }}
      >
        선택한 환자 PDF 전송
      </button>

      <Grid
        data={patients.slice(page.skip, page.skip + page.take)} // 페이지 데이터 처리
        style={{ height: '400px' }}
        sortable
        sort={sort}
        onSortChange={onSortChange}
        pageable={{
          buttonCount: 5,
          pageSizes: [20, 40, 'All'],
          pageSizeValue: pageSizeValue,
        }}
        skip={page.skip}
        take={page.take}
        total={patients.length}
        onPageChange={onPageChange}
      >
        <Column
          headerCell={HeaderCheckboxCell}
          width="50px"
          cell={CheckboxCell}
        />        
        <Column field="id" title="No" width="40px" />
        <Column field="ptCd" title="환자코드" width="100px" />
        <Column field="name" title="이름" cell={DetailLinkCell} width="150px" />
        <Column field="gender" title="성별" width="80px" />
        <Column field="birthDate" title="생년월일" width="120px" />
      </Grid>
    </div>
  );
};

export default PatientListComponent;